"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/overlays";
import {
  createSwotItemAction,
  updateSwotItemAction,
  deleteSwotItemAction,
} from "@/actions/risk-actions";
import {
  Plus,
  Edit2,
  Trash2,
  Shield,
  ShieldAlert,
  TrendingUp,
  AlertTriangle,
  Search,
  HelpCircle,
  Link2,
} from "lucide-react";
import { AiQuadrant } from "./ai-quadrant";

interface SwotItem {
  id: string;
  type: "strength" | "weakness" | "opportunity" | "threat";
  title: string;
  description: string;
  sourceNodes: string[];
  createdAt: string;
}

interface SwotClientProps {
  initialItems: SwotItem[];
}

export function SwotClient({ initialItems }: SwotClientProps) {
  const router = useRouter();
  const [items, setItems] = useState<SwotItem[]>(initialItems);
  const [search, setSearch] = useState("");

  // Modal & Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SwotItem | null>(null);
  const [formType, setFormType] = useState<
    "strength" | "weakness" | "opportunity" | "threat"
  >("strength");
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formSourceNodes, setFormSourceNodes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const handleApplySuggestion = async (
    type: "strength" | "weakness" | "opportunity" | "threat",
    title: string,
    description: string,
  ) => {
    try {
      const res = await createSwotItemAction({
        type,
        title,
        description,
        sourceNodes: [],
      });
      if (!res.error) {
        router.refresh();
        return { success: true };
      } else {
        return { error: res.error };
      }
    } catch (err: any) {
      return { error: err.message || "Errore sconosciuto." };
    }
  };

  const handleOpenModal = (type: SwotItem["type"], item?: SwotItem) => {
    setError("");
    setLoading(false);
    if (item) {
      setEditingItem(item);
      setFormType(item.type);
      setFormTitle(item.title);
      setFormDescription(item.description);
      setFormSourceNodes(item.sourceNodes.join(", "));
    } else {
      setEditingItem(null);
      setFormType(type);
      setFormTitle("");
      setFormDescription("");
      setFormSourceNodes("");
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formTitle.trim()) {
      setError("Il titolo è obbligatorio.");
      return;
    }
    if (!formDescription.trim()) {
      setError("La descrizione è obbligatoria.");
      return;
    }

    const sourceNodesArray = formSourceNodes
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    setLoading(true);
    try {
      let res;
      if (editingItem) {
        res = await updateSwotItemAction(editingItem.id, {
          type: formType,
          title: formTitle,
          description: formDescription,
          sourceNodes: sourceNodesArray,
        });
      } else {
        res = await createSwotItemAction({
          type: formType,
          title: formTitle,
          description: formDescription,
          sourceNodes: sourceNodesArray,
        });
      }

      if (res.error) {
        setError(res.error);
      } else {
        setIsModalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Si è verificato un errore.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questo elemento SWOT?")) return;
    try {
      const res = await deleteSwotItemAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert(err.message || "Errore durante l'eliminazione.");
    }
  };

  // Group items by SWOT category
  const filteredItems = items.filter((item) => {
    return (
      item.title.toLowerCase().includes(search.toLowerCase()) ||
      item.description.toLowerCase().includes(search.toLowerCase())
    );
  });

  const strengths = filteredItems.filter((i) => i.type === "strength");
  const weaknesses = filteredItems.filter((i) => i.type === "weakness");
  const opportunities = filteredItems.filter((i) => i.type === "opportunity");
  const threats = filteredItems.filter((i) => i.type === "threat");

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6">
        {/* Search & Overview Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
            <Input
              placeholder="Cerca tra gli elementi SWOT..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>

          <div className="text-xs text-text-secondary flex gap-4">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />{" "}
              Punti di Forza
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />{" "}
              Debolezze
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />{" "}
              Opportunità
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" />{" "}
              Minacce
            </span>
          </div>
        </div>

        {/* 2x2 SWOT Quadrant Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* STRENGTHS (Punti di Forza) */}
          <div className="flex flex-col border border-emerald-500/20 bg-emerald-500/[0.02] rounded-xl p-5 hover:bg-emerald-500/[0.04] transition-all min-h-[300px]">
            <div className="flex justify-between items-center pb-3 border-b border-emerald-500/10">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-emerald-500/10 rounded-lg text-emerald-600">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-text-primary">
                    Strengths
                  </h2>
                  <p className="text-[10px] text-text-secondary font-medium uppercase tracking-wider">
                    Fattori Interni Positivi
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7 rounded-md border-emerald-500/20 text-emerald-700 hover:bg-emerald-500/10 hover:text-emerald-800"
                onClick={() => handleOpenModal("strength")}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 space-y-3 mt-4 overflow-y-auto scrollbar-thin max-h-[360px] pr-1">
              {strengths.length === 0 ? (
                <div className="text-center py-8 text-xs text-text-muted italic">
                  Nessun punto di forza registrato.
                </div>
              ) : (
                strengths.map((item) => (
                  <SwotItemCard
                    key={item.id}
                    item={item}
                    onEdit={handleOpenModal}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </div>
          </div>

          {/* WEAKNESSES (Debolezze) */}
          <div className="flex flex-col border border-amber-500/20 bg-amber-500/[0.02] rounded-xl p-5 hover:bg-amber-500/[0.04] transition-all min-h-[300px]">
            <div className="flex justify-between items-center pb-3 border-b border-amber-500/10">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-amber-500/10 rounded-lg text-amber-600">
                  <ShieldAlert className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-text-primary">
                    Weaknesses
                  </h2>
                  <p className="text-[10px] text-text-secondary font-medium uppercase tracking-wider">
                    Fattori Interni Negativi
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7 rounded-md border-amber-500/20 text-amber-700 hover:bg-amber-500/10 hover:text-amber-800"
                onClick={() => handleOpenModal("weakness")}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 space-y-3 mt-4 overflow-y-auto scrollbar-thin max-h-[360px] pr-1">
              {weaknesses.length === 0 ? (
                <div className="text-center py-8 text-xs text-text-muted italic">
                  Nessuna debolezza registrata.
                </div>
              ) : (
                weaknesses.map((item) => (
                  <SwotItemCard
                    key={item.id}
                    item={item}
                    onEdit={handleOpenModal}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </div>
          </div>

          {/* OPPORTUNITIES (Opportunità) */}
          <div className="flex flex-col border border-blue-500/20 bg-blue-500/[0.02] rounded-xl p-5 hover:bg-blue-500/[0.04] transition-all min-h-[300px]">
            <div className="flex justify-between items-center pb-3 border-b border-blue-500/10">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-500/10 rounded-lg text-blue-600">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-text-primary">
                    Opportunities
                  </h2>
                  <p className="text-[10px] text-text-secondary font-medium uppercase tracking-wider">
                    Fattori Esterni Positivi
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7 rounded-md border-blue-500/20 text-blue-700 hover:bg-blue-500/10 hover:text-blue-800"
                onClick={() => handleOpenModal("opportunity")}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 space-y-3 mt-4 overflow-y-auto scrollbar-thin max-h-[360px] pr-1">
              {opportunities.length === 0 ? (
                <div className="text-center py-8 text-xs text-text-muted italic">
                  Nessuna opportunità registrata.
                </div>
              ) : (
                opportunities.map((item) => (
                  <SwotItemCard
                    key={item.id}
                    item={item}
                    onEdit={handleOpenModal}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </div>
          </div>

          {/* THREATS (Minacce) */}
          <div className="flex flex-col border border-rose-500/20 bg-rose-500/[0.02] rounded-xl p-5 hover:bg-rose-500/[0.04] transition-all min-h-[300px]">
            <div className="flex justify-between items-center pb-3 border-b border-rose-500/10">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-rose-500/10 rounded-lg text-rose-600">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-text-primary">
                    Threats
                  </h2>
                  <p className="text-[10px] text-text-secondary font-medium uppercase tracking-wider">
                    Fattori Esterni Negativi
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7 rounded-md border-rose-500/20 text-rose-700 hover:bg-rose-500/10 hover:text-rose-800"
                onClick={() => handleOpenModal("threat")}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 space-y-3 mt-4 overflow-y-auto scrollbar-thin max-h-[360px] pr-1">
              {threats.length === 0 ? (
                <div className="text-center py-8 text-xs text-text-muted italic">
                  Nessuna minaccia registrata.
                </div>
              ) : (
                threats.map((item) => (
                  <SwotItemCard
                    key={item.id}
                    item={item}
                    onEdit={handleOpenModal}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* AI Suggestions section */}
        <AiQuadrant items={items} onApplySuggestion={handleApplySuggestion} />

        {/* SWOT Add/Edit Dialog Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingItem
                  ? "Modifica Elemento SWOT"
                  : "Aggiungi Elemento SWOT"}
              </DialogTitle>
              <DialogDescription>
                Inserisci i dettagli del fattore strategico per completare
                l&apos;analisi SWOT.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 my-2">
              <div className="space-y-1.5">
                <Label htmlFor="swot-type">Categoria SWOT</Label>
                <Select
                  id="swot-type"
                  value={formType}
                  onChange={(e) => setFormType(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="strength">Strengths (Punti di Forza)</option>
                  <option value="weakness">Weaknesses (Debolezze)</option>
                  <option value="opportunity">
                    Opportunities (Opportunità)
                  </option>
                  <option value="threat">Threats (Minacce)</option>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="swot-title">Titolo Fattore</Label>
                <Input
                  id="swot-title"
                  placeholder="es. Brand reputation forte nel mercato B2B"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="swot-desc">Descrizione & Analisi</Label>
                <textarea
                  id="swot-desc"
                  placeholder="Fornisci dettagli sull'impatto di questo fattore..."
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  disabled={loading}
                  className="flex w-full rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground min-h-[90px]"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1">
                  <Label htmlFor="swot-nodes">
                    Nodi strategici collegati (Tag)
                  </Label>
                  <Tooltip>
                    <TooltipTrigger
                      type="button"
                      className="text-text-muted hover:text-foreground"
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Aggiungi ID di obiettivi, progetti o relazioni per linkare
                      questo elemento SWOT nel grafo aziendale.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Input
                  id="swot-nodes"
                  placeholder="es. goal_checkout_101, proj_redesign_202 (separati da virgola)"
                  value={formSourceNodes}
                  onChange={(e) => setFormSourceNodes(e.target.value)}
                  disabled={loading}
                />
              </div>

              {error && <ErrorMessage>{error}</ErrorMessage>}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsModalOpen(false)}
                  disabled={loading}
                >
                  Annulla
                </Button>
                <Button type="submit" loading={loading}>
                  {editingItem ? "Salva Modifiche" : "Aggiungi Elemento"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

// Inner Component for SWOT item rendering
interface SwotItemCardProps {
  item: SwotItem;
  onEdit: (type: SwotItem["type"], item: SwotItem) => void;
  onDelete: (id: string) => void;
}

function SwotItemCard({ item, onEdit, onDelete }: SwotItemCardProps) {
  const getHeaderBorderColor = (type: SwotItem["type"]) => {
    switch (type) {
      case "strength":
        return "border-emerald-500/20 hover:border-emerald-500/40";
      case "weakness":
        return "border-amber-500/20 hover:border-amber-500/40";
      case "opportunity":
        return "border-blue-500/20 hover:border-blue-500/40";
      case "threat":
        return "border-rose-500/20 hover:border-rose-500/40";
    }
  };

  return (
    <Card
      className={`group relative transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)] border ${getHeaderBorderColor(item.type)} bg-card`}
    >
      <CardHeader className="p-4 pb-2 flex flex-row items-start justify-between space-y-0 gap-2">
        <h4 className="text-sm font-bold text-text-primary leading-tight group-hover:text-primary-600 transition-colors">
          {item.title}
        </h4>
        <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity absolute top-3.5 right-3">
          <button
            onClick={() => onEdit(item.type, item)}
            className="text-text-secondary hover:text-foreground transition-colors p-1 cursor-pointer"
            title="Modifica"
          >
            <Edit2 className="h-3 w-3" />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="text-error hover:text-error/85 transition-colors p-1 cursor-pointer"
            title="Elimina"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-2">
        <p className="text-xs text-text-secondary leading-normal whitespace-pre-line">
          {item.description}
        </p>

        {item.sourceNodes && item.sourceNodes.length > 0 && (
          <div className="flex flex-wrap gap-1 items-center pt-2">
            <Link2 className="h-3 w-3 text-text-muted shrink-0" />
            {item.sourceNodes.map((nodeId) => (
              <Tooltip key={nodeId}>
                <TooltipTrigger asChild>
                  <Link href={`/explore?nodeId=${nodeId}`}>
                    <span className="text-[9px] px-1.5 py-0.5 font-mono font-semibold bg-card-muted hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 text-text-muted border border-border/40 rounded cursor-pointer transition-all">
                      {nodeId.substring(0, 10)}
                      {nodeId.length > 10 ? "..." : ""}
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="top">
                  Impatto associato al nodo: {nodeId}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
